import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

export interface PagedResult<T> {
  items: T[];
  page: number;
  pageSize: number;
  totalCount: number;
}

export type ListQueryStatus = 'loading' | 'empty' | 'error' | 'data';

export type SortDir = 'asc' | 'desc';

export interface UseListQueryOptions<T> {
  /**
   * Fetches one page. The portal passes `useApi().get` directly — `@xcentral/ui` can't
   * cleanly import an app-level hook, so the caller supplies the fetch function instead.
   * `useApi().get<T>` already matches this signature: `(path, signal?) => Promise<T>`.
   */
  fetcher: (url: string, signal?: AbortSignal) => Promise<PagedResult<T>>;
  /** Base API path (no query string), e.g. `/api/v1/accounts`. */
  endpoint: string;
  defaultPageSize?: number;
  defaultSortBy?: string;
  defaultSortDir?: SortDir;
  /** Debounce applied to `search` before it drives the URL + the fetch. Default 300ms. */
  debounceMs?: number;
  /** Query-param name used for the search term. Default `'search'`. */
  searchParamName?: string;
  /**
   * Filter keys to hydrate from the URL on mount (e.g. `['status', 'tier']`). Filters set
   * via `setFilter` are always written out to the URL regardless of this list — it only
   * controls which params are read back in on first mount (arbitrary/unrelated query
   * params on the page are left alone).
   */
  filterKeys?: string[];
  /**
   * Initial filter values applied on mount when the corresponding URL param is absent (e.g.
   * `{ role: 'client' }` to default a list to a client-first view). A URL param always wins,
   * so a shared/bookmarked link is honoured over the default.
   */
  defaultFilters?: Record<string, string>;
  /**
   * Query-param keys this hook does NOT own and must carry through untouched when it rewrites
   * the URL. The write below builds a fresh `URLSearchParams` from the hook's own state, so
   * anything else already on the URL is dropped — which silently breaks a list rendered inside
   * a URL-synced host. Concretely (Story #13147): `AccountDetail` keeps its active tab in
   * `?tab=`, so the first write from a list-shell tab erased `tab=invoices` and snapped the
   * page back to Overview, unmounting the list. Pass `['tab']` there. Preserved params are
   * URL-only — they are never appended to the fetch URL.
   */
  preserveParams?: string[];
  /**
   * Query params that are a property of the MOUNT, not of the user's filter state — always sent
   * with every fetch and never clearable from the UI. Applied AFTER `filters`, so a fixed param
   * always wins a name clash.
   *
   * Story #15798 — `EmailTriagePanel` is mounted twice against one endpoint and each mount is
   * scoped server-side to a mailbox PURPOSE (`?purpose=SalesMarketing` / `?purpose=Support`).
   * That scope deliberately does NOT go through `filters`/`defaultFilters`: `clearFilters()`
   * resets `filters` to `{}`, which would drop the param and silently widen the queue to every
   * mailbox — and the "Clear filters" control in `DataListShell` is one click away. A scope the
   * user can erase is not a scope, so it lives here instead.
   *
   * Entries with an `undefined`/empty value are omitted, so an unscoped mount can pass the prop
   * through unconditionally and get today's unscoped request.
   */
  fixedParams?: Record<string, string | undefined>;
}

export interface UseListQueryResult<T> {
  data: PagedResult<T> | null;
  status: ListQueryStatus;
  error: string | null;
  search: string;
  filters: Record<string, string>;
  page: number;
  pageSize: number;
  sortBy?: string;
  sortDir?: SortDir;
  setSearch: (value: string) => void;
  setFilter: (key: string, value: string) => void;
  clearFilters: () => void;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  /** `dir` undefined clears the sort (the third click of the tri-state cycle). */
  setSort: (sortBy: string | undefined, sortDir: SortDir | undefined) => void;
  refetch: () => void;
}

/**
 * List-query state for `DataListShell` (#13136) — search / filters / paging / sorting,
 * synced to the URL query string (so a list view survives refresh + is shareable/back-
 * button-able), debounced search, `AbortController`-cancelled in-flight requests, and a
 * four-state (`loading`/`empty`/`error`/`data`) result.
 *
 * URL sync is one-directional after mount: the hook reads its initial state from the URL
 * once (lazy `useState` initialisers) and thereafter writes committed state changes back
 * out via `history.replaceState` (never `pushState` — a keystroke or a page click isn't a
 * navigation event). It does not re-subscribe to further external URL edits after mount;
 * within a single mounted list view the hook itself is the source of truth.
 *
 * That write REPLACES the whole query string, so any param the hook doesn't own is dropped
 * unless the caller names it in `preserveParams` (Story #13147). A list rendered inside a
 * URL-synced host — e.g. `AccountDetail`'s `?tab=` — must pass it.
 */
export function useListQuery<T>(options: UseListQueryOptions<T>): UseListQueryResult<T> {
  const {
    fetcher,
    endpoint,
    defaultPageSize = 50,
    defaultSortBy,
    defaultSortDir,
    debounceMs = 300,
    searchParamName = 'search',
    filterKeys = [],
    defaultFilters,
    preserveParams,
    fixedParams,
  } = options;

  const [searchParams, setSearchParams] = useSearchParams();

  const [search, setSearchState] = useState(() => searchParams.get(searchParamName) ?? '');
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [filters, setFiltersState] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = { ...defaultFilters };
    for (const key of filterKeys) {
      const value = searchParams.get(key);
      if (value) initial[key] = value; // a URL param always wins over a default
    }
    return initial;
  });
  const [page, setPageState] = useState(() => Number(searchParams.get('page')) || 1);
  const [pageSize, setPageSizeState] = useState(() => Number(searchParams.get('pageSize')) || defaultPageSize);
  const [sortBy, setSortByState] = useState<string | undefined>(() => searchParams.get('sortBy') ?? defaultSortBy);
  const [sortDir, setSortDirState] = useState<SortDir | undefined>(
    () => (searchParams.get('sortDir') as SortDir | null) ?? defaultSortDir,
  );

  const [data, setData] = useState<PagedResult<T> | null>(null);
  const [status, setStatus] = useState<ListQueryStatus>('loading');
  const [error, setError] = useState<string | null>(null);
  const [refetchTick, setRefetchTick] = useState(0);

  // Ref-forward the router's setter. `setSearchParams`'s identity can change whenever
  // `searchParams` changes (react-router recomputes it) — including as a *result* of a
  // call we just made ourselves. Depending on it directly in the effect below would risk
  // an update loop (effect fires → calls setSearchParams → identity changes → effect
  // fires again). Reading it via a ref sidesteps that without needing the identity at all.
  const setSearchParamsRef = useRef(setSearchParams);
  setSearchParamsRef.current = setSearchParams;

  // `preserveParams` is read through a ref for the same reason, plus one of its own: callers
  // pass an inline array literal (`preserveParams={['tab']}`), whose identity changes every
  // render. Listing it in the fetch effect's deps would therefore re-fire that effect — and
  // its fetch — on every render. It only affects what gets written to the URL, never what is
  // fetched, so reading the latest value via a ref is both correct and loop-free.
  const preserveParamsRef = useRef(preserveParams);
  preserveParamsRef.current = preserveParams;

  // `fixedParams` has `preserveParams`' identity problem — callers pass an inline object literal
  // (`fixedParams={{ purpose }}`), whose identity changes every render — but NOT its escape
  // hatch: this one is part of the fetch URL, so a ref would let the effect fetch a stale scope.
  // Serialising to a sorted, stable string gives the effect a dependency that changes exactly
  // when the scope actually changes, and the effect parses it back rather than closing over the
  // object.
  const fixedParamsKey = JSON.stringify(
    Object.entries(fixedParams ?? {})
      .filter((entry): entry is [string, string] => Boolean(entry[1]))
      .sort((a, b) => a[0].localeCompare(b[0])),
  );

  // Debounce: commit `search` → `debouncedSearch` after `debounceMs` of no further calls.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), debounceMs);
    return () => clearTimeout(t);
  }, [search, debounceMs]);

  // Reset to page 1 whenever the *committed* search term changes (not on every keystroke —
  // that would fire a wasted intermediate fetch for the old search term before the
  // debounce settles).
  const isFirstSearchCommit = useRef(true);
  useEffect(() => {
    if (isFirstSearchCommit.current) {
      isFirstSearchCommit.current = false;
      return;
    }
    setPageState(1);
  }, [debouncedSearch]);

  const setSearch = useCallback((value: string) => setSearchState(value), []);

  const setFilter = useCallback((key: string, value: string) => {
    setFiltersState(prev => {
      const next = { ...prev };
      if (value) next[key] = value;
      else delete next[key];
      return next;
    });
    setPageState(1);
  }, []);

  const clearFilters = useCallback(() => {
    setFiltersState({});
    setPageState(1);
  }, []);

  const setPage = useCallback((next: number) => setPageState(next), []);

  const setPageSize = useCallback((size: number) => {
    setPageSizeState(size);
    setPageState(1);
  }, []);

  const setSort = useCallback((nextSortBy: string | undefined, nextSortDir: SortDir | undefined) => {
    setSortByState(nextSortBy);
    setSortDirState(nextSortDir);
    setPageState(1);
  }, []);

  const refetch = useCallback(() => setRefetchTick(t => t + 1), []);

  // The single effect that both mirrors committed state into the URL and fetches the
  // corresponding page. Combined (rather than two effects) so there is exactly one place
  // that builds the query string.
  useEffect(() => {
    const params = new URLSearchParams();
    if (debouncedSearch) params.set(searchParamName, debouncedSearch);
    for (const [key, value] of Object.entries(filters)) {
      if (value) params.set(key, value);
    }
    params.set('page', String(page));
    params.set('pageSize', String(pageSize));
    if (sortBy) params.set('sortBy', sortBy);
    if (sortDir) params.set('sortDir', sortDir);

    // Mount-fixed params last: a filter must never be able to override the mount's scope.
    for (const [key, value] of JSON.parse(fixedParamsKey) as [string, string][]) {
      params.set(key, value);
    }

    // Carry through the caller-declared params this hook doesn't own (see `preserveParams`).
    // The updater form reads the CURRENT location rather than closing over `searchParams`,
    // so the effect still needn't depend on the router's state identity.
    setSearchParamsRef.current(
      prev => {
        const next = new URLSearchParams(params);
        for (const key of preserveParamsRef.current ?? []) {
          if (next.has(key)) continue; // the hook's own state wins on a name clash
          const existing = prev.get(key);
          if (existing !== null) next.set(key, existing);
        }
        return next;
      },
      { replace: true },
    );

    const controller = new AbortController();
    let cancelled = false;
    setStatus('loading');
    setError(null);

    fetcher(`${endpoint}?${params.toString()}`, controller.signal)
      .then(result => {
        if (cancelled) return;
        setData(result);
        setStatus(result.items.length === 0 ? 'empty' : 'data');
      })
      .catch(err => {
        if (cancelled) return;
        setStatus('error');
        setError(err instanceof Error ? err.message : 'Failed to load');
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [fetcher, endpoint, debouncedSearch, filters, page, pageSize, sortBy, sortDir, refetchTick, searchParamName, fixedParamsKey]);

  return {
    data,
    status,
    error,
    search,
    filters,
    page,
    pageSize,
    sortBy,
    sortDir,
    setSearch,
    setFilter,
    clearFilters,
    setPage,
    setPageSize,
    setSort,
    refetch,
  };
}
